import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { MockApiService } from '@zopup/zop-core';
import { WorkOrderDto } from '@zopup/zop-types';
import { generateMockWorkOrders } from './data';

// The real WorkOrdersService calls an absolute URL (`${MANUFACTURING_API_BASE_PATH}/WorkOrders/...`).
// `:scheme` / `:host` are wildcard segments (MockApiService treats any `:`-prefixed
// segment as "match anything") so these handlers work regardless of which environment
// (local gateway, Azure dev container, etc.) resolves that base path.
const workOrdersUrl = (path = ''): string => `:scheme//:host/WorkOrders${path}`;

@Injectable({ providedIn: 'root' })
export class WorkOrdersMockApi {
    private _workOrders: WorkOrderDto[] = generateMockWorkOrders();

    constructor(private _zopMockApiService: MockApiService) {
        this.registerHandlers();
    }

    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ WorkOrders - GET (paged list) - registered before the `:id` handler so the literal
        // "GetPagedList" segment is matched first
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onGet(workOrdersUrl('/GetPagedList')).reply(({ request }) => {
            const searchText = (request.params.get('SearchText') || '').toLowerCase();
            const status = request.params.get('Status') || '';
            const type = request.params.get('Type') || '';
            const pageNumber = Number(request.params.get('PageNumber')) || 1;
            const pageSize = Number(request.params.get('PageSize')) || 50;
            const orderBy = request.params.getAll('OrderBy') || [];
            const isDescending = request.params.get('IsOrderByDescending') === 'true';

            let rows = cloneDeep(this._workOrders);

            if (status) {
                rows = rows.filter((wo) => wo.status === status);
            }

            if (type) {
                rows = rows.filter((wo) => wo.type === type);
            }

            if (searchText) {
                rows = rows.filter(
                    (wo) =>
                        wo.number?.toLowerCase().includes(searchText) ||
                        wo.subjectId?.toLowerCase().includes(searchText) ||
                        wo.scope?.toLowerCase().includes(searchText)
                );
            }

            if (orderBy.length) {
                const [field] = orderBy;
                rows.sort((a, b) => {
                    const aValue = String((a as any)[field] ?? '');
                    const bValue = String((b as any)[field] ?? '');
                    return isDescending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                });
            }

            const totalCount = rows.length;
            const start = (pageNumber - 1) * pageSize;
            const paged = rows.slice(start, start + pageSize);

            return [200, { isSuccess: true, result: paged, totalCount }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkOrders - GET by id
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onGet(workOrdersUrl('/:id')).reply(({ urlParams }) => {
            const workOrder = this._workOrders.find((wo) => wo.id === urlParams['id']);
            if (!workOrder) {
                return [200, { isSuccess: false, errors: [{ message: 'Work order not found' }] }];
            }
            return [200, { isSuccess: true, result: cloneDeep(workOrder) }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkOrders - POST (create)
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPost(workOrdersUrl()).reply(({ request }) => {
            const workOrderInput = request.body?.workOrder ?? {};
            const newWorkOrder: WorkOrderDto = {
                id: `wo-${this._workOrders.length + 1}-${Date.now()}`,
                status: 'Draft',
                version: 1,
                ...workOrderInput,
            };
            this._workOrders = [newWorkOrder, ...this._workOrders];
            return [200, { isSuccess: true, result: cloneDeep(newWorkOrder) }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ WorkOrders - PUT status transitions
        // -----------------------------------------------------------------------------------------------------
        this._zopMockApiService.onPut(workOrdersUrl('/Plan')).reply(({ request }) => this._setStatus(request.body, 'Planned'));
        this._zopMockApiService.onPut(workOrdersUrl('/Release')).reply(({ request }) => this._setStatus(request.body, 'Released'));
        this._zopMockApiService.onPut(workOrdersUrl('/Start')).reply(({ request }) => this._setStatus(request.body, 'InProgress'));
        this._zopMockApiService.onPut(workOrdersUrl('/Hold')).reply(({ request }) => this._setStatus(request.body, 'OnHold'));
        this._zopMockApiService.onPut(workOrdersUrl('/Resume')).reply(({ request }) => this._setStatus(request.body, 'InProgress'));
        this._zopMockApiService.onPut(workOrdersUrl('/Complete')).reply(({ request }) => this._setStatus(request.body, 'Completed'));
        this._zopMockApiService.onPut(workOrdersUrl('/Close')).reply(({ request }) => this._setStatus(request.body, 'Closed'));
        this._zopMockApiService.onPut(workOrdersUrl('/Cancel')).reply(({ request }) => this._setStatus(request.body, 'Cancelled'));
    }

    private _setStatus(body: { id?: string; version?: number } | undefined, status: WorkOrderDto['status']): [number, unknown] {
        const workOrder = this._workOrders.find((wo) => wo.id === body?.id);
        if (!workOrder) {
            return [200, { isSuccess: false, errors: [{ message: 'Work order not found' }] }];
        }
        workOrder.status = status;
        workOrder.version = (workOrder.version ?? 1) + 1;
        if (status === 'InProgress' && !workOrder.actualStart) {
            workOrder.actualStart = new Date();
        }
        if (status === 'Completed' && !workOrder.actualEnd) {
            workOrder.actualEnd = new Date();
        }
        return [200, { isSuccess: true, result: cloneDeep(workOrder) }];
    }
}
